// src/components/admin/AdminAchievements.js
import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../services/firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, getDocs, doc, setDoc, updateDoc,
  deleteDoc, serverTimestamp
} from 'firebase/firestore';

const CATEGORIES = [
  { value: 'kheladu', label: 'खेळाडू (Athlete)' },
  { value: 'vidyarthi', label: 'विद्यार्थी (Student)' },
  { value: 'tapasvi', label: 'तपस्वी (Disciplined)' },
  { value: 'gaavkari', label: 'गावकरी (Community)' },
];

const CRITERIA_BY_CATEGORY = {
  kheladu: [
    { value: 'totalFitnessDays', label: 'Total Fitness Days', unit: 'days' },
    { value: 'totalSquats', label: 'Total Squats', unit: 'squats' },
    { value: 'totalJumpingJacks', label: 'Total Jumping Jacks', unit: 'reps' },
  ],
  vidyarthi: [
    { value: 'totalLekhiDays', label: 'Total Lekhi Days', unit: 'days' },
    { value: 'totalTopicsCovered', label: 'Total Topics Covered', unit: 'topics' },
    { value: 'grammarTopicsCovered', label: 'Grammar Topics Covered', unit: 'topics' },
    { value: 'mathTopicsCovered', label: 'Math Topics Covered', unit: 'topics' },
    { value: 'reasoningTopicsCovered', label: 'Reasoning Topics Covered', unit: 'topics' },
    { value: 'gkTopicsCovered', label: 'GK Topics Covered', unit: 'topics' },
    { value: 'testsCompleted', label: 'Sunday Tests Completed', unit: 'tests' },
    { value: 'highestTestScore', label: 'Highest Test Score (Best Ever)', unit: 'marks (out of 60)' },
  ],
  tapasvi: [
    { value: 'unifiedStreak', label: 'Daily Streak (Fitness or Lekhi)', unit: 'days' },
    { value: 'dualDays', label: 'Both Fitness + Lekhi Same Day', unit: 'days' },
    { value: 'campsCompleted', label: 'Camps Completed', unit: 'camps' },
  ],
  gaavkari: [
    { value: 'villageTotalPoints', label: "Village's Total Points", unit: 'points' },
    { value: 'userPointsContributed', label: 'User Points Contributed to Village', unit: 'points' },
  ],
};

const EMPTY_FORM = {
  category: 'kheladu',
  titleMr: '', titleEn: '',
  descriptionMr: '', descriptionEn: '',
  criteriaType: 'totalFitnessDays',
  criteriaValue: '',
  active: true,
  celebrate: false
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default function AdminAchievements() {
  const [section, setSection] = useState('achievements');
  const [statusMsg, setStatusMsg] = useState(null);

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  return (
    <div>
      <div className="admin-section-title">Achievements</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { id: 'achievements', label: 'Achievements' },
          { id: 'templates', label: 'Badge Templates' },
          { id: 'users', label: 'User Achievements' },
        ].map(s => (
          <button key={s.id}
            className={`admin-btn ${section === s.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>
      {statusMsg && (
        <div className={`admin-status-msg ${statusMsg.type}`} style={{ marginBottom: '16px' }}>
          {statusMsg.text}
        </div>
      )}
      {section === 'achievements' && <AchievementsList showMsg={showMsg} />}
      {section === 'templates' && <BadgeTemplates showMsg={showMsg} />}
      {section === 'users' && <UserAchievements showMsg={showMsg} />}
    </div>
  );
}

function AchievementsList({ showMsg }) {
  const [achievements, setAchievements] = useState([]);
  const [unlockCounts, setUnlockCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expandedEdit, setExpandedEdit] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [achSnap, unlockSnap] = await Promise.all([
        getDocs(collection(db, 'achievements')),
        getDocs(collection(db, 'userAchievements'))
      ]);
      setAchievements(achSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const counts = {};
      unlockSnap.docs.forEach(d => {
        const aid = d.data().achievementId;
        counts[aid] = (counts[aid] || 0) + 1;
      });
      setUnlockCounts(counts);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category, isEdit = false) => {
    const firstCriteria = CRITERIA_BY_CATEGORY[category][0].value;
    if (isEdit) {
      setEditForm(prev => ({ ...prev, category, criteriaType: firstCriteria }));
    } else {
      setForm(prev => ({ ...prev, category, criteriaType: firstCriteria }));
    }
  };

  const isDuplicate = (f, excludeId = null) => {
    return achievements.some(a =>
      a.id !== excludeId &&
      a.category === f.category &&
      a.criteriaType === f.criteriaType &&
      parseInt(a.criteriaValue) === parseInt(f.criteriaValue)
    );
  };

  const handleSave = async () => {
    if (!form.titleMr || !form.titleEn || !form.criteriaValue) {
      showMsg('Title (both languages) and criteria value required', 'error'); return;
    }
    if (isDuplicate(form)) {
      showMsg('An achievement with same category, criteria type and value already exists', 'error'); return;
    }
    if (!window.confirm('Add this achievement?')) return;
    setLoading(true);
    try {
      const newRef = doc(collection(db, 'achievements'));
      await setDoc(newRef, {
        category: form.category,
        title: { mr: form.titleMr, en: form.titleEn },
        description: { mr: form.descriptionMr, en: form.descriptionEn },
        criteriaType: form.criteriaType,
        criteriaValue: parseInt(form.criteriaValue),
        active: form.active,
        celebrate: form.celebrate,
        order: achievements.filter(a => a.category === form.category).length,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      showMsg('Achievement added');
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async (id) => {
    if (!editForm.titleMr || !editForm.titleEn || !editForm.criteriaValue) {
      showMsg('Title (both languages) and criteria value required', 'error'); return;
    }
    if (isDuplicate(editForm, id)) {
      showMsg('An achievement with same category, criteria type and value already exists', 'error'); return;
    }
    if (!window.confirm('Save changes?')) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'achievements', id), {
        category: editForm.category,
        title: { mr: editForm.titleMr, en: editForm.titleEn },
        description: { mr: editForm.descriptionMr, en: editForm.descriptionEn },
        criteriaType: editForm.criteriaType,
        criteriaValue: parseInt(editForm.criteriaValue),
        active: editForm.active,
        celebrate: editForm.celebrate,
        updatedAt: serverTimestamp()
      });
      showMsg('Achievement updated');
      setExpandedEdit(null);
      setEditForm(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this achievement permanently? Users who already unlocked it will keep it.')) return;
    try {
      await deleteDoc(doc(db, 'achievements', id));
      showMsg('Achievement deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  const handleToggleActive = async (ach) => {
    if (!window.confirm(`Mark as ${ach.active ? 'inactive' : 'active'}?`)) return;
    try {
      await updateDoc(doc(db, 'achievements', ach.id), { active: !ach.active });
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  const handleDragStart = (id) => { dragItem.current = id; };
  const handleDragOver = (e, id) => { e.preventDefault(); dragOver.current = id; };
  const handleDrop = async (category) => {
    if (!dragItem.current || !dragOver.current || dragItem.current === dragOver.current) return;
    const catAchs = achievements.filter(a => a.category === category)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const from = catAchs.findIndex(a => a.id === dragItem.current);
    const to = catAchs.findIndex(a => a.id === dragOver.current);
    if (from === -1 || to === -1) return;
    const reordered = [...catAchs];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    for (let i = 0; i < reordered.length; i++) {
      await updateDoc(doc(db, 'achievements', reordered[i].id), { order: i });
    }
    setAchievements(prev => {
      const others = prev.filter(a => a.category !== category);
      return [...others, ...reordered.map((a, i) => ({ ...a, order: i }))];
    });
    dragItem.current = null;
    dragOver.current = null;
  };

  const getCriteriaUnit = (criteriaType) => {
    for (const criteria of Object.values(CRITERIA_BY_CATEGORY)) {
      const found = criteria.find(c => c.value === criteriaType);
      if (found) return found.unit;
    }
    return '';
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = achievements
      .filter(a => a.category === cat.value)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    return acc;
  }, {});

  const currentCriteria = CRITERIA_BY_CATEGORY[form.category] || [];

  return (
    <div>
      <div className="admin-card">
        <h3>Add New Achievement</h3>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Category</label>
            <select value={form.category} onChange={e => handleCategoryChange(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="admin-form-group">
            <label>Criteria Type</label>
            <select value={form.criteriaType} onChange={e => setForm({ ...form, criteriaType: e.target.value })}>
              {currentCriteria.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="admin-form-group">
            <label>Criteria Value ({getCriteriaUnit(form.criteriaType)})</label>
            <input type="number" min="1" value={form.criteriaValue}
              onChange={e => setForm({ ...form, criteriaValue: e.target.value })}
              placeholder="e.g. 7" />
          </div>
        </div>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Title (Marathi)</label>
            <input type="text" value={form.titleMr}
              onChange={e => setForm({ ...form, titleMr: e.target.value })}
              placeholder="सलग ७ दिवस" />
          </div>
          <div className="admin-form-group">
            <label>Title (English)</label>
            <input type="text" value={form.titleEn}
              onChange={e => setForm({ ...form, titleEn: e.target.value })}
              placeholder="7 Day Streak" />
          </div>
        </div>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Description (Marathi)</label>
            <textarea value={form.descriptionMr}
              onChange={e => setForm({ ...form, descriptionMr: e.target.value })}
              style={{ minHeight: '60px', resize: 'vertical' }}
              placeholder="सलग ७ दिवस सराव केला" />
          </div>
          <div className="admin-form-group">
            <label>Description (English)</label>
            <textarea value={form.descriptionEn}
              onChange={e => setForm({ ...form, descriptionEn: e.target.value })}
              style={{ minHeight: '60px', resize: 'vertical' }}
              placeholder="Trained 7 days in a row" />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '12px' }}>
          <input type="checkbox" checked={form.active}
            onChange={e => setForm({ ...form, active: e.target.checked })} />
          Active
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '12px' }}>
          <input type="checkbox" checked={form.celebrate}
            onChange={e => setForm({ ...form, celebrate: e.target.checked })} />
          Show celebration popup (fireworks + claps) on unlock
        </label>
        {(form.titleEn || form.titleMr) && (
          <div style={{ background: '#f0f4ff', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '13px' }}>
            <span style={{ color: '#2563eb', fontWeight: 600 }}>Preview: </span>
            {form.titleEn || form.titleMr} — {CRITERIA_BY_CATEGORY[form.category]?.find(c => c.value === form.criteriaType)?.label}
            {form.criteriaValue ? ` ≥ ${form.criteriaValue} ${getCriteriaUnit(form.criteriaType)}` : ''}
          </div>
        )}
        <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Add Achievement'}
        </button>
      </div>

      {CATEGORIES.map(cat => (
        <div key={cat.value} className="admin-card">
          <h3>{cat.label} ({grouped[cat.value]?.length || 0}) — drag to reorder</h3>
          {grouped[cat.value]?.length === 0 && (
            <p style={{ color: '#999', fontSize: '14px' }}>No achievements in this category yet.</p>
          )}
          {grouped[cat.value]?.map(ach => (
            <React.Fragment key={ach.id}>
              <div
                draggable
                onDragStart={() => handleDragStart(ach.id)}
                onDragOver={e => handleDragOver(e, ach.id)}
                onDrop={() => handleDrop(cat.value)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px', marginBottom: '4px',
                  background: ach.active ? '#f8f9fa' : '#fef2f2',
                  borderRadius: '8px', border: '1px solid #e9ecef', cursor: 'grab' }}>
                <span style={{ color: '#aaa' }}>⠿</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{ach.title?.en}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{ach.title?.mr}</div>
                  <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                    {CRITERIA_BY_CATEGORY[ach.category]?.find(c => c.value === ach.criteriaType)?.label} ≥ {ach.criteriaValue} {getCriteriaUnit(ach.criteriaType)}
                  </div>
                </div>
                <div style={{ textAlign: 'center', minWidth: '60px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#2563eb' }}>
                    {unlockCounts[ach.id] || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>unlocked</div>
                </div>
                <span style={{ fontSize: '12px', color: ach.active ? '#28a745' : '#dc3545', fontWeight: 600, minWidth: '55px' }}>
                  {ach.active ? 'Active' : 'Inactive'}
                </span>
                <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
                  onClick={() => {
                    if (expandedEdit === ach.id) { setExpandedEdit(null); setEditForm(null); return; }
                    setExpandedEdit(ach.id);
                    setEditForm({
                      category: ach.category, titleMr: ach.title?.mr || '',
                      titleEn: ach.title?.en || '', descriptionMr: ach.description?.mr || '',
                      descriptionEn: ach.description?.en || '', criteriaType: ach.criteriaType,
                      criteriaValue: ach.criteriaValue, active: ach.active,
                      celebrate: ach.celebrate || false
                    });
                  }}>
                  {expandedEdit === ach.id ? 'Cancel' : 'Edit'}
                </button>
                <button className="admin-btn" style={{ padding: '4px 10px', fontSize: '13px',
                  background: ach.active ? '#fff3cd' : '#d4edda', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  onClick={() => handleToggleActive(ach)}>
                  {ach.active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
                  onClick={() => handleDelete(ach.id)}>
                  Delete
                </button>
              </div>

              {expandedEdit === ach.id && editForm && (
                <div style={{ background: '#f0f4ff', borderRadius: '8px', padding: '16px',
                  marginBottom: '8px', border: '1px solid #2563eb33' }}>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Category</label>
                      <select value={editForm.category} onChange={e => handleCategoryChange(e.target.value, true)}>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="admin-form-group">
                      <label>Criteria Type</label>
                      <select value={editForm.criteriaType}
                        onChange={e => setEditForm({ ...editForm, criteriaType: e.target.value })}>
                        {(CRITERIA_BY_CATEGORY[editForm.category] || []).map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-form-group">
                      <label>Criteria Value ({getCriteriaUnit(editForm.criteriaType)})</label>
                      <input type="number" min="1" value={editForm.criteriaValue}
                        onChange={e => setEditForm({ ...editForm, criteriaValue: e.target.value })} />
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Title (Marathi)</label>
                      <input type="text" value={editForm.titleMr}
                        onChange={e => setEditForm({ ...editForm, titleMr: e.target.value })} />
                    </div>
                    <div className="admin-form-group">
                      <label>Title (English)</label>
                      <input type="text" value={editForm.titleEn}
                        onChange={e => setEditForm({ ...editForm, titleEn: e.target.value })} />
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Description (Marathi)</label>
                      <textarea value={editForm.descriptionMr}
                        onChange={e => setEditForm({ ...editForm, descriptionMr: e.target.value })}
                        style={{ minHeight: '60px', resize: 'vertical' }} />
                    </div>
                    <div className="admin-form-group">
                      <label>Description (English)</label>
                      <textarea value={editForm.descriptionEn}
                        onChange={e => setEditForm({ ...editForm, descriptionEn: e.target.value })}
                        style={{ minHeight: '60px', resize: 'vertical' }} />
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '12px' }}>
                    <input type="checkbox" checked={editForm.active}
                      onChange={e => setEditForm({ ...editForm, active: e.target.checked })} />
                    Active
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '12px' }}>
                    <input type="checkbox" checked={editForm.celebrate}
                      onChange={e => setEditForm({ ...editForm, celebrate: e.target.checked })} />
                    Show celebration popup (fireworks + claps) on unlock
                  </label>
                  <button className="admin-btn admin-btn-primary"
                    onClick={() => handleEditSave(ach.id)} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

function downloadBlankTemplate() {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1440;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, 1080, 1440);
  ctx.strokeStyle = '#e9ecef';
  ctx.lineWidth = 4;
  ctx.strokeRect(20, 20, 1040, 1400);

  const drawZone = (x, y, w, h, label, sublabel) => {
    ctx.setLineDash([12, 8]);
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
    ctx.fillStyle = '#e6f1fb';
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#0c447c';
    ctx.font = 'bold 36px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(label, x + w / 2, y + h / 2 - 10);
    if (sublabel) {
      ctx.fillStyle = '#378add';
      ctx.font = '28px Arial';
      ctx.fillText(sublabel, x + w / 2, y + h / 2 + 36);
    }
  };

  // Photo circle — center (540, 450), radius 230
  const photoRadius = 230;
  const photoCenterX = 540;
  const photoCenterY = 450;
  ctx.setLineDash([12, 8]);
  ctx.strokeStyle = '#2563eb';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(photoCenterX, photoCenterY, photoRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#e6f1fb';
  ctx.beginPath();
  ctx.arc(photoCenterX, photoCenterY, photoRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#0c447c';
  ctx.font = 'bold 36px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('USER PHOTO', photoCenterX, photoCenterY - 10);
  ctx.fillStyle = '#378add';
  ctx.font = '28px Arial';
  ctx.fillText('460×460px circle', photoCenterX, photoCenterY + 36);

  // Name zone — text baseline drawn at y=790
  drawZone(80, 740, 920, 80, 'USER FULL NAME', 'bold, centered');

  // Title zone — text baseline drawn at y=1080
  drawZone(80, 1030, 920, 80, 'ACHIEVEMENT TITLE', 'bold, centered');

  // Description zone — text starts at y=1200, 700px wide
  drawZone(190, 1150, 700, 200, 'ACHIEVEMENT DESCRIPTION', '2-4 lines, centered');

  ctx.fillStyle = '#888';
  ctx.font = '24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Canvas size: 1080 × 1440px', 540, 1400);

  const link = document.createElement('a');
  link.download = 'spocademy_badge_template.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
}

function BadgeTemplates({ showMsg }) {
  const [templates, setTemplates] = useState({});
  const [uploading, setUploading] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const snap = await getDocs(collection(db, 'achievementTemplates'));
      const data = {};
      snap.docs.forEach(d => { data[d.id] = d.data(); });
      setTemplates(data);
    } catch (e) {}
  };

  const handleUpload = async (category, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showMsg('File must be under 2MB', 'error'); return; }
    if (!window.confirm(`Upload badge template for ${CATEGORIES.find(c => c.value === category)?.label}?`)) return;
    setUploading(category);
    try {
      const storageRef = ref(storage, `achievement_templates/${category}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await setDoc(doc(db, 'achievementTemplates', category), {
        category, imageURL: url, updatedAt: serverTimestamp()
      });
      showMsg('Template uploaded');
      load();
    } catch (e) {
      showMsg('Error uploading: ' + e.message, 'error');
    } finally {
      setUploading(null);
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h3>Badge Template Spec</h3>
        <p style={{ fontSize: '13px', color: '#888', lineHeight: '1.6' }}>
          Design your templates in Canva at <strong>1080 × 1440px</strong> (portrait).<br />
          Leave these zones completely empty in your design:<br />
          — Top center: user photo circle (348×348px)<br />
          — Below photo: user full name<br />
          — Center: achievement title<br />
          — Below title: achievement description<br />
          Add your colors, graphics, and branding everywhere else.
        </p>
        <button className="admin-btn admin-btn-primary" style={{ marginTop: '12px' }}
          onClick={downloadBlankTemplate}>
          Download Blank Template
        </button>
      </div>
      {CATEGORIES.map(cat => (
        <div key={cat.value} className="admin-card">
          <h3>{cat.label}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {templates[cat.value]?.imageURL ? (
              <img src={templates[cat.value].imageURL} alt={cat.label}
                style={{ width: '80px', height: '142px', objectFit: 'cover',
                  borderRadius: '8px', border: '1px solid #e9ecef' }} />
            ) : (
              <div style={{ width: '80px', height: '142px', background: '#f8f9fa',
                borderRadius: '8px', border: '2px dashed #e9ecef',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', color: '#aaa', textAlign: 'center', padding: '8px' }}>
                No template yet
              </div>
            )}
            <div>
              <label style={{ fontSize: '14px', color: '#555', marginBottom: '8px', display: 'block' }}>
                {templates[cat.value] ? 'Replace template (max 2MB)' : 'Upload template (max 2MB)'}
              </label>
              <input type="file" accept="image/*"
                onChange={e => handleUpload(cat.value, e.target.files[0])}
                disabled={uploading === cat.value} />
              {uploading === cat.value && (
                <div style={{ fontSize: '13px', color: '#2563eb', marginTop: '8px' }}>Uploading...</div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UserAchievements({ showMsg }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'unlockedAt', direction: 'desc' });
  const [filterCategory, setFilterCategory] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [userSnap, achSnap, unlockSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'achievements')),
        getDocs(collection(db, 'userAchievements'))
      ]);
      const users = {};
      userSnap.docs.forEach(d => { users[d.id] = d.data(); });
      const achs = {};
      achSnap.docs.forEach(d => { achs[d.id] = d.data(); });
      const rows = unlockSnap.docs.map(d => {
        const item = d.data();
        const user = users[item.userId] || {};
        const ach = achs[item.achievementId] || {};
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
        return {
          id: d.id,
          userName: fullName,
          village: user.village || '—',
          achievementTitle: ach.title?.en || item.achievementId,
          category: ach.category || '—',
          unlockedAt: item.unlockedAt,
        };
      });
      setData(rows);
      setLoaded(true);
    } catch (e) {
      showMsg('Error loading', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (key) => sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const getSorted = () => {
    let filtered = filterCategory === 'all' ? data : data.filter(r => r.category === filterCategory);
    return [...filtered].sort((a, b) => {
      let av = a[sortConfig.key] ?? '';
      let bv = b[sortConfig.key] ?? '';
      if (sortConfig.key === 'unlockedAt') { av = av?.seconds || 0; bv = bv?.seconds || 0; }
      else { if (typeof av === 'string') av = av.toLowerCase(); if (typeof bv === 'string') bv = bv.toLowerCase(); }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const formatDate = (ts) => {
    if (!ts?.seconds) return '—';
    return new Date(ts.seconds * 1000).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="admin-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ margin: 0 }}>User Achievements {loaded ? `(${getSorted().length})` : ''}</h3>
        <button className="admin-btn admin-btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading...' : loaded ? 'Refresh' : 'Load Data'}
        </button>
      </div>
      {!loaded && !loading && (
        <p style={{ color: '#999', fontSize: '14px' }}>Click "Load Data" to view unlocked achievements.</p>
      )}
      {loaded && (
        <div>
          <div className="admin-form-group" style={{ maxWidth: '200px', marginBottom: '16px' }}>
            <label>Filter by Category</label>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {getSorted().length === 0 ? (
            <p style={{ color: '#999', fontSize: '14px' }}>No achievements unlocked yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('userName')} style={{ cursor: 'pointer' }}>User{sortArrow('userName')}</th>
                    <th onClick={() => handleSort('village')} style={{ cursor: 'pointer' }}>Village{sortArrow('village')}</th>
                    <th onClick={() => handleSort('achievementTitle')} style={{ cursor: 'pointer' }}>Achievement{sortArrow('achievementTitle')}</th>
                    <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Category{sortArrow('category')}</th>
                    <th onClick={() => handleSort('unlockedAt')} style={{ cursor: 'pointer' }}>Unlocked{sortArrow('unlockedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {getSorted().map(row => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 500 }}>{row.userName}</td>
                      <td>{row.village}</td>
                      <td>{row.achievementTitle}</td>
                      <td style={{ fontSize: '12px', color: '#2563eb' }}>
                        {CATEGORIES.find(c => c.value === row.category)?.label || row.category}
                      </td>
                      <td style={{ fontSize: '13px' }}>{formatDate(row.unlockedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}